import { useState, useRef, useCallback } from 'react'
import { Button, Input, Select, SignupCta, Toggle, Modal, ExpandableCard, CurrencyInput } from '@/components/ui'
import { SEO } from '@/components/seo'
import { GrowthLineChart, DonutChart, DonutLegend } from '@/components/charts'
import { PDFDocument } from '@/components/pdf'
import BrandingPanel from '@/components/BrandingPanel'
import { BrandingProvider, useBranding } from '@/contexts'
import { usePDFGenerator } from '@/hooks'
import {
  calculateAdviceValue,
  generateProjectionTimeline,
  calculateValueAttribution,
  formatCurrency,
  getYearsToRetirement,
} from '@/utils/calculations'
import { VANGUARD_ALPHA, ILC_WEALTH_UPLIFT } from '@/data/valueOfAdvice'
import type {
  CalculatorInputs,
  CalculatorMode,
  CalculationResults,
  TimelineDataPoint,
  ValueAttribution,
  PortfolioBreakdown,
  ReportData,
  RiskProfile,
} from '@/types'

// ============================================
// Icons
// ============================================

function CheckIcon() {
  return (
    <svg className="w-5 h-5 text-accent-teal flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CalculatorIcon() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="8" y1="10" x2="10" y2="10" />
      <line x1="14" y1="10" x2="16" y2="10" />
      <line x1="8" y1="14" x2="10" y2="14" />
      <line x1="14" y1="14" x2="16" y2="14" />
      <line x1="8" y1="18" x2="10" y2="18" />
      <line x1="14" y1="18" x2="16" y2="18" />
    </svg>
  )
}

function TrendingUpIcon() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  )
}

function DownloadIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="7 10 12 15 17 10" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="12" y1="15" x2="12" y2="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function PaletteIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="13.5" cy="6.5" r="0.5" fill="currentColor" />
      <circle cx="17.5" cy="10.5" r="0.5" fill="currentColor" />
      <circle cx="8.5" cy="7.5" r="0.5" fill="currentColor" />
      <circle cx="6.5" cy="12.5" r="0.5" fill="currentColor" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.555C21.965 6.012 17.461 2 12 2z" />
    </svg>
  )
}

// ============================================
// Inner Component (with branding context)
// ============================================

function ValueOfAdvicePageInner() {
  const { branding } = useBranding()
  const pdfGenerator = usePDFGenerator()
  const pdfContainerRef = useRef<HTMLDivElement>(null)

  // Mode and form state
  const [mode, setMode] = useState<CalculatorMode>('quick')
  const [inputs, setInputs] = useState<CalculatorInputs>({
    clientName: '',
    currentAge: 45,
    retirementAge: 65,
    portfolioValue: 250000,
    annualContribution: 12000,
    riskProfile: 'balanced',
  })
  const [portfolioBreakdown, setPortfolioBreakdown] = useState<PortfolioBreakdown>({
    pension: 150000,
    isa: 60000,
    gia: 40000,
  })

  // Results state
  const [results, setResults] = useState<CalculationResults | null>(null)
  const [timeline, setTimeline] = useState<TimelineDataPoint[]>([])
  const [attributions, setAttributions] = useState<ValueAttribution[]>([])
  const [hasCalculated, setHasCalculated] = useState(false)

  // UI state
  const [showPreview, setShowPreview] = useState(false)
  const [showBranding, setShowBranding] = useState(false)

  // Handle input changes
  const handleInputChange = useCallback((field: keyof CalculatorInputs, value: string | number) => {
    setInputs((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleBreakdownChange = useCallback((field: keyof PortfolioBreakdown, value: number) => {
    setPortfolioBreakdown((prev) => ({ ...prev, [field]: value }))
  }, [])

  // Calculate results
  const handleCalculate = useCallback(() => {
    const years = getYearsToRetirement(inputs.currentAge, inputs.retirementAge)
    const portfolioValue =
      mode === 'advanced'
        ? portfolioBreakdown.pension + portfolioBreakdown.isa + portfolioBreakdown.gia
        : inputs.portfolioValue

    const calculatedResults = calculateAdviceValue(
      portfolioValue,
      inputs.annualContribution,
      years,
      inputs.riskProfile
    )

    const calculatedTimeline = generateProjectionTimeline(
      portfolioValue,
      inputs.annualContribution,
      years,
      inputs.riskProfile
    )

    const calculatedAttributions = calculateValueAttribution(calculatedResults.valueAdded)

    setResults(calculatedResults)
    setTimeline(calculatedTimeline)
    setAttributions(calculatedAttributions)
    setHasCalculated(true)
  }, [inputs, mode, portfolioBreakdown])

  // Prepare report data
  const getReportData = useCallback((): ReportData | null => {
    if (!results) return null

    const portfolioValue =
      mode === 'advanced'
        ? portfolioBreakdown.pension + portfolioBreakdown.isa + portfolioBreakdown.gia
        : inputs.portfolioValue

    return {
      clientName: inputs.clientName,
      portfolioValue,
      annualContribution: inputs.annualContribution,
      years: results.years,
      riskProfile: inputs.riskProfile,
      results,
      timeline,
      attributions,
      generatedAt: new Date(),
    }
  }, [results, inputs, mode, portfolioBreakdown, timeline, attributions])

  // Handle PDF download
  const handleDownloadPDF = useCallback(async () => {
    const reportData = getReportData()
    if (!reportData || !pdfContainerRef.current) return

    await pdfGenerator.generatePDF(reportData, branding, pdfContainerRef.current)
  }, [getReportData, branding, pdfGenerator])

  const riskOptions = [
    { value: 'cautious', label: 'Cautious (4% base return)' },
    { value: 'balanced', label: 'Balanced (6% base return)' },
    { value: 'adventurous', label: 'Adventurous (8% base return)' },
  ]

  const totalPortfolio =
    mode === 'advanced'
      ? portfolioBreakdown.pension + portfolioBreakdown.isa + portfolioBreakdown.gia
      : inputs.portfolioValue

  const reportData = getReportData()

  return (
    <>
      <SEO
        title="Value of Advice Calculator | WealthSpace"
        description="Calculate and demonstrate the true value of financial advice. Research-backed reports showing client ROI using Vanguard and ILC data."
        keywords="value of advice, financial advice value, adviser value proposition, client ROI, Vanguard adviser alpha, ILC research, advice value calculator"
        url="/value-of-advice"
      />

      {/* Hero Section */}
      <section className="pt-[100px] pb-[60px] px-6 text-center relative overflow-hidden">

        <div className="max-w-[800px] mx-auto relative z-[1]">
          <div className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-4 py-2 mb-6">
            <CalculatorIcon />
            <span className="text-sm text-brand-muted">Research-Backed Calculator</span>
          </div>

          <h1 className="font-serif text-[clamp(36px,5vw,52px)] font-normal leading-[1.1] text-brand-light mb-6">
            Demonstrate the <span className="text-accent-teal italic">value</span> of your advice
          </h1>
          <p className="text-xl text-brand-muted leading-[1.6] max-w-[600px] mx-auto">
            Use research from Vanguard and the ILC to show clients the tangible benefits of
            professional financial advice.
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="px-6 pb-20 max-w-[1400px] mx-auto">
        {/* Mode Toggle */}
        <div className="flex justify-center mb-8">
          <Toggle
            options={['Quick', 'Advanced']}
            value={mode === 'quick' ? 'Quick' : 'Advanced'}
            onChange={(v) => setMode(v === 'Quick' ? 'quick' : 'advanced')}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Input Form */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-8">
            <h2 className="font-serif text-2xl text-brand-light mb-2">Client Details</h2>
            <p className="text-brand-muted mb-8">
              Enter your client&apos;s details to calculate the projected value of advice.
            </p>

            <div className="space-y-6">
              <Input
                label="Client Name (Optional)"
                placeholder="e.g., John Smith"
                value={inputs.clientName}
                onChange={(e) => handleInputChange('clientName', e.target.value)}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Current Age"
                  type="number"
                  min={18}
                  max={100}
                  value={inputs.currentAge}
                  onChange={(e) => handleInputChange('currentAge', parseInt(e.target.value) || 0)}
                />
                <Input
                  label="Retirement Age"
                  type="number"
                  min={18}
                  max={100}
                  value={inputs.retirementAge}
                  onChange={(e) => handleInputChange('retirementAge', parseInt(e.target.value) || 0)}
                />
              </div>

              {mode === 'quick' ? (
                <CurrencyInput
                  label="Current Portfolio Value"
                  value={inputs.portfolioValue}
                  onChange={(value) => handleInputChange('portfolioValue', value)}
                />
              ) : (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-brand-muted">Portfolio Breakdown</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <CurrencyInput
                      label="Pension"
                      value={portfolioBreakdown.pension}
                      onChange={(value) => handleBreakdownChange('pension', value)}
                    />
                    <CurrencyInput
                      label="ISA"
                      value={portfolioBreakdown.isa}
                      onChange={(value) => handleBreakdownChange('isa', value)}
                    />
                    <CurrencyInput
                      label="GIA/Other"
                      value={portfolioBreakdown.gia}
                      onChange={(value) => handleBreakdownChange('gia', value)}
                    />
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg flex justify-between items-center">
                    <span className="text-sm text-brand-muted">Total Portfolio</span>
                    <span className="text-lg font-semibold text-accent-teal">
                      {formatCurrency(totalPortfolio)}
                    </span>
                  </div>
                </div>
              )}

              <CurrencyInput
                label="Annual Contribution"
                value={inputs.annualContribution}
                onChange={(value) => handleInputChange('annualContribution', value)}
              />

              <Select
                label="Risk Profile"
                options={riskOptions}
                value={inputs.riskProfile}
                onChange={(e) => handleInputChange('riskProfile', e.target.value as RiskProfile)}
              />

              <Button onClick={handleCalculate} className="w-full justify-center">
                <TrendingUpIcon />
                Calculate Value of Advice
              </Button>
            </div>
          </div>

          {/* Results Display */}
          <div className="space-y-6">
            {!hasCalculated ? (
              <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-8 text-center min-h-[400px] flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-accent-teal/10 rounded-full flex items-center justify-center mb-6">
                  <TrendingUpIcon />
                </div>
                <h3 className="font-serif text-xl text-brand-light mb-2">Ready to calculate</h3>
                <p className="text-brand-muted max-w-[300px]">
                  Enter your client&apos;s details and click calculate to see the projected value of advice.
                </p>
              </div>
            ) : (
              results && (
                <>
                  {/* Main Value Card */}
                  <div className="bg-gradient-to-br from-accent-teal to-emerald-500 rounded-2xl p-8 text-white">
                    <p className="text-sm font-medium opacity-80 mb-2">
                      Projected Value of Advice over {results.years} years
                    </p>
                    <p className="font-serif text-5xl font-semibold mb-4">
                      {formatCurrency(results.valueAdded)}
                    </p>
                    <p className="text-sm opacity-80">
                      That&apos;s {results.percentageGain}% more than without professional advice
                    </p>
                    <p className="text-xs opacity-70 mt-2">
                      Illustrative, not guaranteed. Based on research assumptions; actual results will vary.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4">
                    <Button
                      onClick={() => setShowPreview(true)}
                      variant="secondary"
                      className="flex-1 justify-center"
                    >
                      Preview Report
                    </Button>
                    <Button
                      onClick={handleDownloadPDF}
                      className="flex-1 justify-center"
                      disabled={pdfGenerator.isGenerating}
                    >
                      <DownloadIcon />
                      {pdfGenerator.isGenerating ? `Generating... ${pdfGenerator.progress}%` : 'Download PDF'}
                    </Button>
                    <button
                      onClick={() => setShowBranding(!showBranding)}
                      className="p-3 bg-slate-50 border border-slate-200 rounded-lg hover:border-accent-teal/50 transition-colors"
                      title="Customize Branding"
                    >
                      <PaletteIcon />
                    </button>
                  </div>

                  {/* Charts */}
                  <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
                    <h4 className="text-sm font-semibold text-brand-light mb-4">Growth Projection</h4>
                    <GrowthLineChart
                      timeline={timeline}
                      primaryColor={branding.primaryColor}
                      theme={branding.theme}
                    />
                  </div>

                  {/* Stat Cards */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
                      <p className="text-sm text-brand-muted mb-1">With Advice</p>
                      <p className="font-serif text-2xl text-accent-teal">
                        {formatCurrency(results.withAdvice)}
                      </p>
                    </div>
                    <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
                      <p className="text-sm text-brand-muted mb-1">Without Advice</p>
                      <p className="font-serif text-2xl text-brand-light">
                        {formatCurrency(results.withoutAdvice)}
                      </p>
                    </div>
                  </div>

                  {/* Value Attribution */}
                  <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
                    <h4 className="text-sm font-semibold text-brand-light mb-4">Value Attribution</h4>
                    <div className="grid grid-cols-2 gap-6">
                      <DonutChart attributions={attributions} theme={branding.theme} />
                      <div className="flex flex-col justify-center">
                        <DonutLegend attributions={attributions} />
                      </div>
                    </div>
                  </div>

                  {/* Research Sources */}
                  <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
                    <h4 className="text-sm font-semibold text-brand-light mb-4">Based on Research</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <CheckIcon />
                        <div>
                          <p className="text-sm text-brand-light">Vanguard Adviser&apos;s Alpha</p>
                          <p className="text-xs text-brand-muted">~3% annual value add potential</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckIcon />
                        <div>
                          <p className="text-sm text-brand-light">ILC/Royal London Study</p>
                          <p className="text-xs text-brand-muted">
                            {formatCurrency(ILC_WEALTH_UPLIFT.overallUplift)} average 10-year uplift
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )
            )}
          </div>
        </div>
      </section>

      {/* Value Components Section */}
      <section className="px-6 py-20 bg-slate-50">
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-[clamp(28px,4vw,40px)] font-normal text-brand-light mb-4">
              Where the <span className="text-accent-teal">value</span> comes from
            </h2>
            <p className="text-brand-muted max-w-[600px] mx-auto">
              Vanguard&apos;s research identifies key areas where professional advice adds
              measurable value. Click each component to learn more.
            </p>
          </div>

          <div className="space-y-4">
            {VANGUARD_ALPHA.components.map((component, index) => (
              <ExpandableCard
                key={component.id}
                title={component.name}
                subtitle={`+${component.valuePercent}% annual value (${component.valueBps} basis points)`}
                defaultOpen={index === 0}
                headerContent={
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${component.color}20` }}
                  >
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: component.color }} />
                  </div>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                  {/* What it means */}
                  <div>
                    <h4 className="font-semibold mb-3" style={{ color: component.color }}>
                      What it means
                    </h4>
                    <p className="text-sm text-brand-muted leading-relaxed">
                      {component.description}
                    </p>
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-brand-subtle">Annual value add</div>
                      <div className="text-xl font-semibold" style={{ color: component.color }}>
                        +{component.valuePercent}%
                      </div>
                    </div>
                  </div>

                  {/* Value indicator */}
                  <div>
                    <h4 className="font-semibold mb-3 text-brand-light">Potential benefit</h4>
                    <div
                      className="p-4 rounded-lg"
                      style={{
                        background: `${component.color}10`,
                        borderLeft: `4px solid ${component.color}`,
                      }}
                    >
                      <div className="text-2xl font-semibold" style={{ color: component.color }}>
                        {component.valueBps} bps
                      </div>
                      <div className="text-sm text-brand-muted mt-1">Per year of advice</div>
                    </div>
                    <p className="mt-3 text-xs text-brand-subtle">
                      This component represents approximately{' '}
                      {Math.round((component.valuePercent / VANGUARD_ALPHA.totalPotential) * 100 / 100)}%
                      of the total ~3% annual value potential.
                    </p>
                  </div>
                </div>
              </ExpandableCard>
            ))}
          </div>

          {/* Total Value Summary */}
          <div className="mt-8 p-6 bg-gradient-to-br from-accent-teal/10 to-emerald-500/10 border border-accent-teal/20 rounded-xl text-center">
            <p className="text-sm text-brand-muted mb-2">Total potential value add</p>
            <p className="text-4xl font-serif text-accent-teal">~3%</p>
            <p className="text-sm text-brand-muted mt-2">per year, according to Vanguard&apos;s Adviser&apos;s Alpha research</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20">
        <div className="max-w-[800px] mx-auto">
          <div className="bg-gradient-to-br from-accent-teal to-emerald-500 rounded-2xl p-12 text-center text-white">
            <h2 className="font-serif text-3xl font-normal mb-4">Ready to demonstrate your value?</h2>
            <p className="text-lg opacity-80 mb-8 max-w-[500px] mx-auto">
              WealthSpace helps you create professional, research-backed value of advice reports for
              your clients.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <SignupCta
                location="value_of_advice_cta"
                variant="secondary"
                className="!bg-white/90 hover:!bg-white !text-slate-900 !border-white"
              >
                Start Free Trial
              </SignupCta>
              <Button
                href="/pricing"
                variant="secondary"
                className="!border-white/50 !text-white hover:!bg-white/10"
              >
                View Pricing
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="px-6 pb-20">
        <div className="max-w-[800px] mx-auto">
          <p className="text-xs text-brand-subtle text-center leading-relaxed">
            The projections shown are for illustrative purposes only and are based on historical
            research data. Past performance is not a reliable indicator of future results. The value
            of investments can go down as well as up, and you may get back less than you invested.
            Sources: Vanguard Adviser&apos;s Alpha (2024), ILC &quot;What It&apos;s Worth&quot; (2019), Royal London
            (2024).
          </p>
        </div>
      </section>

      {/* Branding Panel */}
      <BrandingPanel isOpen={showBranding} onClose={() => setShowBranding(false)} />

      {/* Preview Modal */}
      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title="Report Preview"
        maxWidth="full"
      >
        <div className="p-6 bg-brand-dark/50 overflow-auto max-h-[70vh]">
          {reportData && (
            <div ref={pdfContainerRef} className="mx-auto" style={{ width: '210mm' }}>
              <PDFDocument data={reportData} branding={branding} />
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-4">
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
          <Button onClick={handleDownloadPDF} disabled={pdfGenerator.isGenerating}>
            <DownloadIcon />
            {pdfGenerator.isGenerating ? `Generating... ${pdfGenerator.progress}%` : 'Download PDF'}
          </Button>
        </div>
      </Modal>

      {/* Hidden PDF Container for Generation */}
      {hasCalculated && reportData && !showPreview && (
        <div
          ref={pdfContainerRef}
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            visibility: 'hidden',
          }}
        >
          <PDFDocument data={reportData} branding={branding} />
        </div>
      )}
    </>
  )
}

// ============================================
// Main Component (with Provider)
// ============================================

export default function ValueOfAdvicePage() {
  return (
    <BrandingProvider>
      <ValueOfAdvicePageInner />
    </BrandingProvider>
  )
}
